import {
  BulkFetchParameters,
  DEFAULT_JIRA_FIELDS,
  DEFAULT_MAX_RESULTS,
  GetAllIssuesByJQL,
  GetIssuePageByJQLParams,
  Issue,
  IssueCountByJQLResponse,
  IssueIdSearchResponse,
  IssueSearchResponse,
  MAX_RESULTS_WIHOUT_FIELDS,
} from '@qality/common-model';
import { RequestOptions } from '@qality/common-model/src/AP/AP';
import { batchRequests } from '@qality/common-service';

import API from './API';

export default {
  async getIssuePageByJQL({
    jql,
    nextPageToken = null,
    fields = [],
    maxResults = DEFAULT_MAX_RESULTS,
    retry = true,
  }: GetIssuePageByJQLParams): Promise<IssueSearchResponse> {
    const requestBody = {
      jql,
      nextPageToken,
      ...(fields && fields.length > 0 ? { fields } : {}),
      ...(maxResults ? { maxResults } : {}),
    };

    const requestArgs: RequestOptions = {
      url: '/rest/api/2/search/jql',
      type: 'POST',
      contentType: 'application/json',
      data: JSON.stringify(requestBody),
    };

    const result = await API[retry ? 'request' : 'noRetry'](requestArgs).then(
      (response) => {
        return JSON.parse(response.body);
      }
    );
    return result;
  },

  async bulkFetchIssuesByIds({
    ids,
    maxResults = DEFAULT_MAX_RESULTS,
    fields = DEFAULT_JIRA_FIELDS,
    expand = [],
  }: BulkFetchParameters): Promise<Array<Issue>> {
    const uniqIds = Array.from(new Set(ids)).filter((id) => id);

    const chunks: Array<Array<number | string>> = [];
    for (let i = 0; i < uniqIds.length; i += maxResults) {
      chunks.push(uniqIds.slice(i, i + maxResults));
    }

    const requests = chunks.map(
      (chunk) => () =>
        API.request({
          url: `/rest/api/2/issue/bulkfetch`,
          type: 'POST',
          contentType: 'application/json',
          data: JSON.stringify({
            issueIdsOrKeys: chunk,
            ...(fields.length > 0 ? { fields } : {}),
            ...(expand.length > 0 ? { expand } : {}),
          }),
        }).then((response) => JSON.parse(response.body).issues)
    );

    const result: Array<Array<Issue>> = await batchRequests(requests);
    return result.flat();
  },

  async getAllIssuesByJql({
    jql,
    fields = DEFAULT_JIRA_FIELDS,
    expand = [],
  }: GetAllIssuesByJQL): Promise<Array<Issue>> {
    try {
      let issueIds: Array<string | number> = [];
      let nextPageToken: string = null;

      do {
        const response: IssueIdSearchResponse = await this.getIssuePageByJQL({
          jql,
          nextPageToken,
          maxResults: MAX_RESULTS_WIHOUT_FIELDS,
        });
        issueIds = issueIds.concat(response.issues.map((issue) => issue.id));
        nextPageToken = response.nextPageToken;
      } while (nextPageToken);

      const allIssues = await this.bulkFetchIssuesByIds({
        ids: issueIds,
        fields,
        expand,
      });
      return allIssues;
    } catch (e) {
      return [];
    }
  },

  async countIssuesByJql(jql: string): Promise<number> {
    const requestArgs = {
      url: '/rest/api/2/search/approximate-count',
      type: 'POST',
      contentType: 'application/json',
      data: JSON.stringify({
        jql,
      }),
    };

    const result: IssueCountByJQLResponse = await API.request(requestArgs).then(
      (response) => {
        return JSON.parse(response.body);
      }
    );
    return result.count;
  },
};
